// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Button,
  DataTableSkeleton,
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Add } from '@carbon/icons-react';
import { useOrganizations, useSites, describeApiError } from '../../shared/provisioning';
import styles from './SitesPage.module.css';

export default function SitesPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { data: orgs } = useOrganizations();
  const { data, isLoading, isError, error } = useSites(orgId);

  const org = orgs?.find(o => o.id === orgId);

  return (
    <>
      <div className={styles.head}>
        <h1 className={styles.title}>Sites</h1>
        <span className={styles.org}>
          <Link to="/organizations">Organizations</Link>
          {org ? ` / ${org.preferredName}` : ''}
        </span>
        <span className={styles.spacer} />
        <Button
          renderIcon={Add}
          size="sm"
          onClick={() => navigate(`/organizations/${orgId}/sites/new`)}
        >
          Add site
        </Button>
      </div>

      {isLoading && <DataTableSkeleton columnCount={3} rowCount={4} showHeader={false} />}

      {isError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Could not load sites"
          subtitle={describeApiError(error, { action: 'view sites' }).message}
          hideCloseButton
        />
      )}

      {!isLoading && !isError && data?.length === 0 && (
        <div className={styles.empty}>No sites yet.</div>
      )}

      {!isLoading && !isError && !!data?.length && (
        <Table size="sm" useZebraStyles>
          <TableHead>
            <TableRow>
              <TableHeader>Label</TableHeader>
              <TableHeader>Site key</TableHeader>
              <TableHeader>Repository</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map(site => (
              <TableRow key={site.siteId}>
                <TableCell>
                  <Link to={`/sites/${site.siteId}`}>{site.label}</Link>
                </TableCell>
                <TableCell>{site.siteKey}</TableCell>
                <TableCell className={styles.mono}>
                  {site.physicalKnowledgeGraph?.repoUrl ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}
